import { fetchJSON as fetchAPI } from './client';

export type GuidingPrinciple = {
  key: string;
  name: string;
  description: string;
  mandatory: boolean;
};

export type StandardLayer = {
  key: string;
  name: string;
  description: string;
  required: boolean;
};

export type PlatformDependency = {
  dev_id: string;
  name: string;
  service: string;
  port: number;
  required: boolean;
};

export type ProductFamilyEntry = {
  id: string;
  name: string;
  dev_id?: string;
  status: string;
  lifecycle_stage: string;
  independently_licensable: boolean;
  modules: number;
  compliance_score: number;
  certified: boolean;
  extension_points?: string[];
};

export type CertificationGate = {
  id: string;
  name: string;
  category: string;
  required: boolean;
  description: string;
};

export type ComplianceKPI = {
  key: string;
  name: string;
  value: number;
  target: number;
  unit?: string;
};

export type ModuleManifestRequirements = {
  required_fields: string[];
  optional_fields: string[];
};

export type ComplianceEvaluation = {
  product_family_id: string;
  status: string;
  score: number;
  passed_gates: number;
  total_gates: number;
  gaps?: string[];
  evaluated_at: string;
};

export type ExtensionPoint = {
  dev_id: string;
  name: string;
  description: string;
};

export type ConstitutionDashboard = {
  constitution_id: string;
  tenant_id: string;
  version: string;
  principles: number;
  product_families: number;
  certified_families: number;
  avg_compliance_score: number;
  certification_gates: number;
  extension_points: ExtensionPoint[];
  generated_at: string;
};

export type ConstitutionStatus = {
  constitution_id: string;
  version: string;
  product_families: number;
  certified_families: number;
  avg_compliance_score: number;
  principles: number;
  certification_gates: number;
  mandatory: boolean;
  status: string;
};

const GOVERNANCE_BASE = import.meta.env.VITE_GOVERNANCE_API ?? '/api/v1/governance';

function fetchGovernance<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${GOVERNANCE_BASE}${path}`, init);
}

export const productFamilyConstitutionApi = {
  dashboard: () => fetchGovernance<ConstitutionDashboard>('/product-family/dashboard'),
  principles: () => fetchGovernance<{ principles: GuidingPrinciple[] }>('/product-family/principles').then((r) => r.principles),
  layers: () => fetchGovernance<{ layers: StandardLayer[] }>('/product-family/layers').then((r) => r.layers),
  platformDependencies: () => fetchGovernance<{ dependencies: PlatformDependency[] }>('/product-family/platform-dependencies').then((r) => r.dependencies),
  registry: () => fetchGovernance<{ product_families: ProductFamilyEntry[] }>('/product-family/registry').then((r) => r.product_families),
  certificationGates: () => fetchGovernance<{ certification_gates: CertificationGate[] }>('/product-family/certification-gates').then((r) => r.certification_gates),
  kpis: () => fetchGovernance<{ kpis: ComplianceKPI[] }>('/product-family/kpis').then((r) => r.kpis),
  manifestRequirements: () => fetchGovernance<ModuleManifestRequirements>('/product-family/manifest-requirements'),
  evaluate: (id: string) => fetchGovernance<ComplianceEvaluation>(`/product-family/evaluate/${id}`, { method: 'POST' }),
  validateManifest: (manifest: Record<string, unknown>) =>
    fetchGovernance<{ valid: boolean; missing_fields: string[] }>('/product-family/manifest/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(manifest),
    }),
  status: () => fetchGovernance<ConstitutionStatus>('/product-family/status'),
  extensionPoints: () => fetchGovernance<{ extension_points: ExtensionPoint[] }>('/product-family/extensions').then((r) => r.extension_points),
};
